export interface Translations {
  [key: string]: string;
}

export interface TranslationMap {
  [locale: string]: Translations;
}

export const translations: TranslationMap = {
  en: {
    // Navigation
    'nav.categories': 'Templates',
    'nav.generate': 'Generate',
    'nav.myChecklists': 'My Projects',
    'nav.dashboard': 'Dashboard',
    'nav.profile': 'Profile Settings',
    'nav.admin': 'Admin Panel',
    'nav.signOut': 'Sign Out',
    'nav.login': 'Login',
    'nav.search': 'Search templates...',
    'nav.theme': 'Theme',
    'nav.region': 'Region',
    'nav.home': 'Home',

    // Home Page
    'home.hero.badge': 'AI-Powered Checklists',
    'home.hero.title': '10,000+ Checklists for',
    'home.hero.titleHighlight': 'Every Situation',
    'home.hero.subtitle': 'From moving to a new apartment to planning your wedding, find the perfect checklist or generate your own with AI.',
    'home.hero.cta': 'Browse Categories',
    'home.hero.ctaSecondary': 'Generate Custom Checklist',
    'home.browseByCategory': 'Browse by Category',
    'home.browseByCategory.subtitle': 'Find checklists organized by life area',
    'home.featured': 'Featured Checklists',
    'home.featured.subtitle': 'Popular checklists loved by our users',
    'home.trending': 'Trending Now',
    'home.trending.subtitle': 'What people are checking off this week',
    'home.viewAll': 'View All',
    'home.faq.title': 'Frequently Asked Questions',
    'home.faq.subtitle': 'Everything you need to know about Nexihow',
    'home.cta.title': "Can't Find What You Need?",
    'home.cta.subtitle': 'Use our AI generator to create a custom checklist tailored to your specific needs in seconds.',
    'home.cta.button': 'Generate Your Checklist',
    'home.generator.tryThese': 'Try these',
    'home.generator.examples.moving': '🏠 Moving',
    'home.generator.examples.wedding': '💒 Wedding',
    'home.generator.examples.travel': '✈️ Travel',
    'home.generator.examples.baby': '👶 New Baby',
    'home.generator.tip': 'Be specific for better results — include timeframes, constraints, or your experience level',

    // Category Labels
    'category.label.home': 'Home & Life',
    'category.label.work': 'Work & Business',
    'category.label.money': 'Money & Legal',
    'category.label.travel': 'Travel',
    'category.label.relationships': 'Relationships & Personal',
    'category.label.school': 'School & Learning',
    'category.label.pets': 'Pets & Animals',
    'category.label.diy': 'DIY & Garage',
    'category.label.food': 'Food & Kitchen',
    'category.label.tech': 'Tech & Digital',
    'category.label.health': 'Health & Safety',

    // Category Descriptions
    'category.description.home': 'Everything for your living space, from moving to maintenance',
    'category.description.work': 'Career, business, and professional development',
    'category.description.money': 'Financial planning, budgeting, and legal matters',
    'category.description.travel': 'Packing lists, trip planning, and travel essentials',
    'category.description.relationships': 'Dating, family, events, and personal connections',
    'category.description.school': 'Education, studying, and academic success',
    'category.description.pets': 'Pet care, adoption, and animal essentials',
    'category.description.diy': 'Home improvement, tools, and hands-on projects',
    'category.description.food': 'Kitchen setup, meal planning, and cooking',
    'category.description.tech': 'Digital life, gadgets, and online security',
    'category.description.health': 'Wellness, safety, and healthcare preparation',

    // Categories
    'categories.title': 'All Categories',
    'categories.subtitle': 'Browse our comprehensive collection of checklists organized by life area. From home and work to travel and health, we\'ve got you covered.',
    'categories.checklists': 'checklists',

    // Checklist
    'checklist.items': 'items',
    'checklist.completed': 'completed',
    'checklist.progress': 'Progress',
    'checklist.save': 'Save Checklist',
    'checklist.saved': 'Saved!',
    'checklist.print': 'Print',
    'checklist.share': 'Share',
    'checklist.reset': 'Reset',
    'checklist.copy': 'Copy',
    'checklist.addItem': 'Add Item',

    // Generate
    'generate.badge': 'AI-Powered Generator',
    'generate.title': 'Generate Your Custom Checklist',
    'generate.subtitle': 'Enter any topic and our AI will create a comprehensive checklist tailored to your needs.',
    'generate.inputLabel': 'What do you need a checklist for?',
    'generate.placeholder': 'e.g., Planning a birthday party, Starting a podcast, Preparing for a marathon...',
    'generate.button': 'Generate Checklist',
    'generate.buttonLoggedOut': 'Log In to Generate',
    'generate.generating': 'Generating...',
    'generate.loginRequired': 'Login Required',
    'generate.loginRequiredDesc': 'Please log in to generate checklists',
    'generate.loginPrompt': 'Please log in or create an account to generate checklists. Free users get 5 generations per day.',
    'generate.topicTooLong': 'Topic is too long. Please shorten it to {max} characters.',
    'generate.tips.title': 'Tips for better results:',
    'generate.tips.1': 'Be specific about your goal or situation',
    'generate.tips.2': 'Include any constraints or requirements',
    'generate.tips.3': 'Mention the timeframe if relevant',
    'generate.tips.4': 'Add context about your experience level',
    'generate.saved': 'Checklist saved to your account',
    'generate.generated': 'Created {count} actionable items',

    // Auth
    'auth.login': 'Login',
    'auth.signup': 'Sign Up',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.displayName': 'Display Name (optional)',
    'auth.forgotPassword': 'Forgot Password?',
    'auth.noAccount': "Don't have an account?",
    'auth.hasAccount': 'Already have an account?',
    'auth.welcomeBack': 'Welcome Back',
    'auth.createAccount': 'Create Account',
    'auth.loginDesc': 'Login to access your saved checklists',
    'auth.signupDesc': 'Sign up to save and manage your checklists',
    'auth.loggingIn': 'Logging in...',
    'auth.creatingAccount': 'Creating account...',
    'auth.loginFailed': 'Login Failed',
    'auth.signupFailed': 'Sign Up Failed',
    'auth.invalidCredentials': 'Invalid email or password. Please try again.',
    'auth.resetPassword': 'Reset Password',
    'auth.resetPasswordDesc': 'Enter your email and we\'ll send you a link to reset your password.',
    'auth.sendResetEmail': 'Send Reset Email',
    'auth.sendingResetEmail': 'Sending...',
    'auth.resetEmailSent': 'Reset Email Sent',
    'auth.resetEmailSentDesc': 'Check your email for a link to reset your password.',
    'auth.resetFailed': 'Reset Failed',
    'auth.backToLogin': 'Back to Login',
    'auth.setNewPassword': 'Set New Password',
    'auth.setNewPasswordDesc': 'Enter your new password below.',
    'auth.newPassword': 'New Password',
    'auth.confirmPassword': 'Confirm Password',
    'auth.passwordsDoNotMatch': 'Passwords do not match',
    'auth.updatePassword': 'Update Password',
    'auth.updatingPassword': 'Updating...',
    'auth.updatePasswordFailed': 'Update Failed',
    'auth.passwordUpdated': 'Password Updated',
    'auth.passwordUpdatedDesc': 'Your password has been successfully updated.',
    'auth.redirectingHome': 'Redirecting you to the home page...',
    'auth.resetLinkExpired': 'Reset Link Expired',
    'auth.resetLinkExpiredDesc': 'Please request a new password reset link.',
    'auth.verifyEmailReminder': 'Please verify your email address to access all features.',
    'auth.resendVerification': 'Resend',
    'auth.verificationSent': 'Verification Email Sent',
    'auth.verificationSentDesc': 'Check your inbox for the verification link.',
    'auth.resendFailed': 'Failed to Send',
    'auth.sending': 'Sending...',
    'auth.accountExists': 'An account with this email already exists. Please login instead.',
    'auth.welcomeMessage': 'You have successfully logged in.',
    'auth.accountCreated': 'Welcome to Nexihow!',
    'auth.passwordStrength': 'Password strength',
    'auth.strengthWeak': 'Weak',
    'auth.strengthMedium': 'Medium',
    'auth.strengthStrong': 'Strong',
    'auth.passwordRequirements': 'Password must contain:',
    'auth.req8chars': 'At least 8 characters',
    'auth.reqUppercase': 'One uppercase letter',
    'auth.reqLowercase': 'One lowercase letter',
    'auth.reqNumber': 'One number',
    'auth.reqSpecial': 'One special character',
    'auth.emailValid': 'Valid email',
    'auth.emailInvalid': 'Invalid email format',
    'auth.passwordsMatch': 'Passwords match',
    'auth.passwordsNoMatch': 'Passwords do not match',

    // Dashboard
    'dashboard.title': 'Your Dashboard',
    'dashboard.subtitle': 'Track your progress and stay motivated',
    'dashboard.dayStreak': 'Day Streak',
    'dashboard.bestStreak': 'Best Streak',
    'dashboard.itemsDone': 'Items Done',
    'dashboard.achievements': 'Achievements',
    'dashboard.todayProgress': "Today's Progress",
    'dashboard.dailyGoal': 'Daily Goal',
    'dashboard.goalReached': 'Daily goal reached!',
    'dashboard.upcomingReminders': 'Upcoming Reminders',
    'dashboard.overdueReminders': '{count} overdue reminder(s)',
    'dashboard.noReminders': 'No upcoming reminders',
    'dashboard.setReminders': 'Set reminders on checklist items to see them here',
    'dashboard.moreReminders': '+ {count} more reminders',
    'dashboard.streakProgress': 'Streak Progress',
    'dashboard.today': 'Today',
    'dashboard.tomorrow': 'Tomorrow',

    // Profile
    'profile.title': 'Profile Settings',
    'profile.subtitle': 'Manage your account information and preferences',
    'profile.picture': 'Profile Picture',
    'profile.pictureDesc': 'Upload an image for your profile (max 5MB)',
    'profile.uploadImage': 'Upload Image',
    'profile.uploading': 'Uploading...',
    'profile.avatarUploaded': 'Your profile picture has been updated.',
    'profile.avatarRemoved': 'Your profile picture has been removed.',
    'profile.accountInfo': 'Account Information',
    'profile.accountInfoDesc': 'Update your display name and view account details',
    'profile.displayName': 'Display Name',
    'profile.emailAddress': 'Email Address',
    'profile.emailNote': 'Email address cannot be changed',
    'profile.saveChanges': 'Save Changes',
    'profile.saving': 'Saving...',
    'profile.saved': 'Your profile has been saved successfully.',
    'profile.rateLimited': 'Too many requests. Please wait a few minutes before trying again.',
    'profile.dangerZone': 'Danger Zone',
    'profile.dangerZoneDesc': 'Irreversible actions for your account',
    'profile.deleteAccount': 'Delete Account',
    'profile.deleteAccountWarning': 'Once you delete your account, all of your data will be permanently removed. This action cannot be undone.',
    'profile.deletingAccount': 'Deleting...',
    'profile.deleteAccountConfirmTitle': 'Are you absolutely sure?',
    'profile.deleteAccountConfirmDesc': 'This will permanently delete your account and all associated data including saved checklists, subscriptions, and profile information. This action cannot be undone.',
    'profile.confirmDelete': 'Yes, Delete My Account',
    'profile.accountDeleted': 'Account Deleted',
    'profile.accountDeletedDesc': 'Your account and all associated data have been permanently deleted.',
    'profile.deleteAccountError': 'Failed to delete account. Please try again.',

    // Saved Checklists
    'saved.title': 'My Saved Checklists',
    'saved.count': '{count} checklist(s) saved',
    'saved.generateNew': 'Generate New',
    'saved.noChecklists': 'No saved checklists yet',
    'saved.noChecklistsDesc': 'Generate a checklist and save it to see it here',
    'saved.generateFirst': 'Generate Your First Checklist',
    'saved.moreItems': '+{count} more items',
    'saved.deleted': 'Checklist has been deleted',
    'saved.refreshed': 'Your checklists have been updated',

    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.view': 'View',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.loginToSave': 'Login to Save',
    'common.dismiss': 'Dismiss',

    // Footer
    'footer.tagline': 'Your go-to resource for thousands of AI-generated checklists for every situation in life.',
    'footer.quickLinks': 'Quick Links',
    'footer.allCategories': 'All Categories',
    'footer.generateChecklist': 'Generate Checklist',
    'footer.popularCategories': 'Popular Categories',
    'footer.copyright': '© {year} Nexihow. All rights reserved.',
    'footer.homeLife': 'Home & Life',
    'footer.travel': 'Travel',
    'footer.workBusiness': 'Work & Business',

    // Locale
    'locale.selectRegion': 'Select Region',
    'locale.detecting': 'Detecting location...',

    // 404 Page
    'notFound.title': '404',
    'notFound.message': 'Oops! Page not found',
    'notFound.returnHome': 'Return to Home',

    // Search Page
    'search.title': 'Search Results',
    'search.resultsFor': '{count} result(s) for "{query}"',
    'search.noResults': 'No results found',
    'search.noResultsDesc': 'Try a different search term or browse our categories.',
    'search.browseCategories': 'Browse Categories',
    'search.generateCustom': 'Generate Custom Checklist',

    // Category Page
    'category.notFound': 'Category Not Found',
    'category.notFoundDesc': "The category you're looking for doesn't exist.",
    'category.browseAll': 'Browse all categories',
    'category.checklistsAvailable': '{count} checklist(s) available',
    'category.noChecklists': 'No checklists found in this category yet.',
    'category.generateOne': 'Generate one with AI',

    // Checklist Page
    'checklistPage.notFound': 'Checklist Not Found',
    'checklistPage.notFoundDesc': "The checklist you're looking for doesn't exist.",
    'checklistPage.browseAll': 'Browse all checklists',
    'checklistPage.sound': 'Sound',
    'checklistPage.soundOn': 'On',
    'checklistPage.soundOff': 'Off',
    'checklistPage.ofCompleted': '{checked} of {total} completed',
    'checklistPage.due': 'Due: {date}',
    'checklistPage.congratulations': 'Congratulations!',
    'checklistPage.completedMessage': "You've completed this checklist!",
    'checklistPage.saveCopy': 'Save a Copy',
    'checklistPage.shareAchievement': 'Share Achievement',
    'checklistPage.relatedChecklists': 'Related Checklists',

    // Saved Checklist View
    'savedView.notFound': 'Checklist Not Found',
    'savedView.backToSaved': 'Back to saved checklists',
    'savedView.savedOn': 'Saved on {date}',

    // Breadcrumbs
    'breadcrumb.home': 'Home',
    'breadcrumb.categories': 'Categories',
    'breadcrumb.searchResults': 'Search Results',
    'breadcrumb.myChecklists': 'My Projects',

    // FAQ
    'faq.whatIs.question': 'What is Nexihow?',
    'faq.whatIs.answer': 'Nexihow is an AI-powered tool that helps you structure tasks and processes into clear, actionable plans. Whether for personal organization or team workflows and SOPs, Nexihow turns natural language into clarity in seconds.',
    'faq.forWhom.question': 'Is Nexihow for personal use or for businesses?',
    'faq.forWhom.answer': 'Both. Individuals use Nexihow to organize daily tasks and life events. Teams and businesses use it to create workflows, SOPs, onboarding processes, and repeatable systems — using the same AI engine.',
    'faq.experience.question': 'Do I need experience writing workflows or SOPs?',
    'faq.experience.answer': 'No. You simply describe what you\'re trying to do in plain language. Nexihow automatically structures it into a clear workflow or SOP.',
    'faq.difference.question': 'What\'s the difference between a checklist, workflow, and SOP?',
    'faq.difference.answer': 'A checklist helps complete tasks. A workflow shows how steps relate and flow together. An SOP documents a repeatable process for consistent execution. Nexihow can generate all three from the same input.',
    'faq.teams.question': 'Can Nexihow be used by teams?',
    'faq.teams.answer': 'Yes. With the Team plan, you can collaborate in shared workspaces, manage workflows and SOPs, assign steps, and track progress together.',
    'faq.teamSize.question': 'What happens if my team grows beyond 10 people?',
    'faq.teamSize.answer': 'The Team plan includes up to 10 members. Custom plans are available for larger teams.',
    'faq.privacy.question': 'Is my data private?',
    'faq.privacy.answer': 'Yes. Your data is private and secure. Your content is not shared with other users.',
    'faq.export.question': 'Can I export workflows or SOPs?',
    'faq.export.answer': 'Yes. Premium and Team plans support exporting to formats like PDF and DOCX.',
    'faq.free.question': 'Is there a free plan?',
    'faq.free.answer': 'Yes. You can start for free and upgrade only when you need more advanced features.',
    'faq.unique.question': 'What makes Nexihow different from other productivity tools?',
    'faq.unique.answer': 'Most tools require you to build structure manually. Nexihow creates the structure for you using AI.',
    'faq.solo.question': 'Can I use Nexihow without a team?',
    'faq.solo.answer': 'Absolutely. Many users use Nexihow solo for personal organization or professional work.',
    'faq.install.question': 'Do I need to install anything?',
    'faq.install.answer': 'No. Nexihow runs entirely in your browser and works on desktop and mobile.',

    // Additional missing keys
    'saved.countSaved': '{count} checklist(s) saved',
    'saved.moreItemsCount': '+{count} more items',
    'profile.memberSince': 'Member since: {date}',
    'profile.characters': '{count}/{max} characters',
    'profile.fileTypeError': 'Invalid file type',
    'profile.fileTypeErrorDesc': 'Please upload a JPG, PNG, WebP, or GIF image.',
    'profile.fileSizeError': 'File too large',
    'profile.fileSizeErrorDesc': 'Please upload an image smaller than 5MB.',
    'profile.fileFormatHint': 'JPG, PNG, WebP, or GIF. Max 5MB.',
    'generate.enterItem': 'Enter item...',
    'dashboard.days': 'days',
    'dashboard.overdueCount': '{count} overdue reminder(s)',
    'dashboard.moreRemindersCount': '+ {count} more reminders',
    'locale.selectLanguage': 'Language',

    // Affiliate
    'affiliate.recommended': 'Recommended Products',
    'affiliate.disclosure': 'We may earn a commission from purchases made through these links.',
    'affiliate.shopNow': 'Shop Now',
    'affiliate.sponsored': 'Sponsored',
    'affiliate.essentials': 'Essential Items for Your Checklist',
  },

  de: {
    // Navigation
    'nav.categories': 'Kategorien',
    'nav.generate': 'Erstellen',
    'nav.myChecklists': 'Meine Projekte',
    'nav.dashboard': 'Dashboard',
    'nav.profile': 'Profileinstellungen',
    'nav.admin': 'Admin-Bereich',
    'nav.signOut': 'Abmelden',
    'nav.login': 'Anmelden',
    'nav.search': 'Checklisten durchsuchen...',
    'nav.theme': 'Design',
    'nav.region': 'Region',
    'nav.home': 'Startseite',

    // Home Page
    'home.hero.badge': 'KI-gestützte Checklisten',
    'home.hero.title': '10.000+ Checklisten für',
    'home.hero.titleHighlight': 'jede Situation',
    'home.hero.subtitle': 'Vom Umzug bis zur Hochzeitsplanung - finden Sie die perfekte Checkliste oder erstellen Sie Ihre eigene mit KI.',
    'home.hero.cta': 'Kategorien durchsuchen',
    'home.hero.ctaSecondary': 'Eigene Checkliste erstellen',
    'home.browseByCategory': 'Nach Kategorie durchsuchen',
    'home.browseByCategory.subtitle': 'Checklisten nach Lebensbereich geordnet',
    'home.featured': 'Empfohlene Checklisten',
    'home.featured.subtitle': 'Beliebte Checklisten unserer Nutzer',
    'home.trending': 'Aktuell beliebt',
    'home.trending.subtitle': 'Was diese Woche abgehakt wird',
    'home.viewAll': 'Alle anzeigen',
    'home.faq.title': 'Häufig gestellte Fragen',
    'home.faq.subtitle': 'Alles, was Sie über Nexihow wissen müssen',
    'home.cta.title': 'Nicht gefunden, was Sie suchen?',
    'home.cta.subtitle': 'Nutzen Sie unseren KI-Generator, um in Sekunden eine maßgeschneiderte Checkliste zu erstellen.',
    'home.cta.button': 'Checkliste erstellen',
    'home.generator.tryThese': 'Versuchen Sie diese',
    'home.generator.examples.moving': '🏠 Umzug',
    'home.generator.examples.wedding': '💒 Hochzeit',
    'home.generator.examples.travel': '✈️ Reise',
    'home.generator.examples.baby': '👶 Baby',
    'home.generator.tip': 'Seien Sie spezifisch für bessere Ergebnisse — Zeitrahmen, Anforderungen oder Erfahrungsstufe angeben',

    // Category Labels
    'category.label.home': 'Haus & Leben',
    'category.label.work': 'Arbeit & Geschäft',
    'category.label.money': 'Geld & Recht',
    'category.label.travel': 'Reisen',
    'category.label.relationships': 'Beziehungen & Persönliches',
    'category.label.school': 'Schule & Lernen',
    'category.label.pets': 'Haustiere & Tiere',
    'category.label.diy': 'Heimwerken & Garage',
    'category.label.food': 'Essen & Küche',
    'category.label.tech': 'Technik & Digital',
    'category.label.health': 'Gesundheit & Sicherheit',

    // Category Descriptions
    'category.description.home': 'Alles für Ihren Wohnraum, vom Umzug bis zur Wartung',
    'category.description.work': 'Karriere, Geschäft und berufliche Entwicklung',
    'category.description.money': 'Finanzplanung, Budgetierung und rechtliche Angelegenheiten',
    'category.description.travel': 'Packlisten, Reiseplanung und Reise-Essentials',
    'category.description.relationships': 'Dating, Familie, Veranstaltungen und persönliche Beziehungen',
    'category.description.school': 'Bildung, Lernen und akademischer Erfolg',
    'category.description.pets': 'Tierpflege, Adoption und Tier-Essentials',
    'category.description.diy': 'Heimwerken, Werkzeuge und praktische Projekte',
    'category.description.food': 'Kücheneinrichtung, Mahlzeitenplanung und Kochen',
    'category.description.tech': 'Digitales Leben, Gadgets und Online-Sicherheit',
    'category.description.health': 'Wellness, Sicherheit und Gesundheitsvorsorge',

    // Categories
    'categories.title': 'Alle Kategorien',
    'categories.subtitle': 'Durchsuchen Sie unsere umfassende Sammlung von Checklisten. Von Haushalt und Arbeit bis hin zu Reisen und Gesundheit.',
    'categories.checklists': 'Checklisten',

    // Generate
    'generate.badge': 'KI-Generator',
    'generate.title': 'Erstellen Sie Ihre eigene Checkliste',
    'generate.subtitle': 'Geben Sie ein Thema ein und unsere KI erstellt eine umfassende Checkliste für Sie.',
    'generate.inputLabel': 'Wofür brauchen Sie eine Checkliste?',
    'generate.placeholder': 'z.B. Geburtstagsparty planen, Podcast starten, Marathon vorbereiten...',
    'generate.button': 'Checkliste erstellen',
    'generate.buttonLoggedOut': 'Anmelden zum Erstellen',
    'generate.generating': 'Wird erstellt...',
    'generate.loginRequired': 'Anmeldung erforderlich',
    'generate.tips.title': 'Tipps für bessere Ergebnisse:',
    'generate.tips.1': 'Seien Sie spezifisch mit Ihrem Ziel',
    'generate.tips.2': 'Nennen Sie Einschränkungen oder Anforderungen',
    'generate.tips.3': 'Erwähnen Sie den Zeitrahmen',
    'generate.tips.4': 'Fügen Sie Kontext über Ihre Erfahrung hinzu',

    // Auth
    'auth.login': 'Anmelden',
    'auth.signup': 'Registrieren',
    'auth.welcomeBack': 'Willkommen zurück',
    'auth.createAccount': 'Konto erstellen',
    'auth.loginDesc': 'Melden Sie sich an, um auf Ihre Checklisten zuzugreifen',
    'auth.signupDesc': 'Registrieren Sie sich, um Checklisten zu speichern',
    'auth.loggingIn': 'Anmeldung...',
    'auth.creatingAccount': 'Konto wird erstellt...',
    'auth.noAccount': 'Noch kein Konto?',
    'auth.hasAccount': 'Bereits ein Konto?',
    'auth.email': 'E-Mail',
    'auth.password': 'Passwort',
    'auth.displayName': 'Anzeigename (optional)',
    'auth.forgotPassword': 'Passwort vergessen?',
    'auth.resetPassword': 'Passwort zurücksetzen',
    'auth.resetPasswordDesc': 'Geben Sie Ihre E-Mail ein und wir senden Ihnen einen Link zum Zurücksetzen.',
    'auth.sendResetEmail': 'Reset-E-Mail senden',
    'auth.sendingResetEmail': 'Wird gesendet...',
    'auth.resetEmailSent': 'Reset-E-Mail gesendet',
    'auth.resetEmailSentDesc': 'Überprüfen Sie Ihre E-Mail auf einen Link zum Zurücksetzen.',
    'auth.resetFailed': 'Zurücksetzen fehlgeschlagen',
    'auth.backToLogin': 'Zurück zur Anmeldung',
    'auth.setNewPassword': 'Neues Passwort festlegen',
    'auth.setNewPasswordDesc': 'Geben Sie unten Ihr neues Passwort ein.',
    'auth.newPassword': 'Neues Passwort',
    'auth.confirmPassword': 'Passwort bestätigen',
    'auth.passwordsDoNotMatch': 'Passwörter stimmen nicht überein',
    'auth.updatePassword': 'Passwort aktualisieren',
    'auth.updatingPassword': 'Aktualisierung...',
    'auth.updatePasswordFailed': 'Aktualisierung fehlgeschlagen',
    'auth.passwordUpdated': 'Passwort aktualisiert',
    'auth.passwordUpdatedDesc': 'Ihr Passwort wurde erfolgreich aktualisiert.',
    'auth.redirectingHome': 'Weiterleitung zur Startseite...',
    'auth.resetLinkExpired': 'Reset-Link abgelaufen',
    'auth.resetLinkExpiredDesc': 'Bitte fordern Sie einen neuen Link an.',
    'auth.verifyEmailReminder': 'Bitte bestätigen Sie Ihre E-Mail-Adresse, um alle Funktionen nutzen zu können.',
    'auth.resendVerification': 'Erneut senden',
    'auth.verificationSent': 'Bestätigungsmail gesendet',
    'auth.verificationSentDesc': 'Überprüfen Sie Ihren Posteingang.',
    'auth.resendFailed': 'Senden fehlgeschlagen',
    'auth.sending': 'Wird gesendet...',
    'auth.passwordStrength': 'Passwortstärke',
    'auth.strengthWeak': 'Schwach',
    'auth.strengthMedium': 'Mittel',
    'auth.strengthStrong': 'Stark',
    'auth.passwordRequirements': 'Passwort muss enthalten:',
    'auth.req8chars': 'Mindestens 8 Zeichen',
    'auth.reqUppercase': 'Einen Großbuchstaben',
    'auth.reqLowercase': 'Einen Kleinbuchstaben',
    'auth.reqNumber': 'Eine Zahl',
    'auth.reqSpecial': 'Ein Sonderzeichen',
    'auth.emailValid': 'Gültige E-Mail',
    'auth.emailInvalid': 'Ungültiges E-Mail-Format',
    'auth.passwordsMatch': 'Passwörter stimmen überein',
    'auth.passwordsNoMatch': 'Passwörter stimmen nicht überein',

    // Dashboard
    'dashboard.title': 'Ihr Dashboard',
    'dashboard.subtitle': 'Verfolgen Sie Ihren Fortschritt',
    'dashboard.dayStreak': 'Tages-Serie',
    'dashboard.bestStreak': 'Beste Serie',
    'dashboard.itemsDone': 'Erledigt',
    'dashboard.achievements': 'Erfolge',
    'dashboard.todayProgress': 'Fortschritt heute',
    'dashboard.dailyGoal': 'Tagesziel',
    'dashboard.goalReached': 'Tagesziel erreicht!',
    'dashboard.upcomingReminders': 'Kommende Erinnerungen',
    'dashboard.noReminders': 'Keine Erinnerungen',
    'dashboard.setReminders': 'Erinnerungen zu Checklisten-Einträgen hinzufügen',
    'dashboard.streakProgress': 'Serien-Fortschritt',
    'dashboard.today': 'Heute',
    'dashboard.tomorrow': 'Morgen',

    // Profile
    'profile.title': 'Profileinstellungen',
    'profile.subtitle': 'Verwalten Sie Ihre Kontoinformationen',
    'profile.picture': 'Profilbild',
    'profile.pictureDesc': 'Laden Sie ein Bild hoch (max 5MB)',
    'profile.uploadImage': 'Bild hochladen',
    'profile.uploading': 'Wird hochgeladen...',
    'profile.accountInfo': 'Kontoinformationen',
    'profile.accountInfoDesc': 'Aktualisieren Sie Ihren Anzeigenamen',
    'profile.displayName': 'Anzeigename',
    'profile.emailAddress': 'E-Mail-Adresse',
    'profile.emailNote': 'E-Mail kann nicht geändert werden',
    'profile.saveChanges': 'Änderungen speichern',
    'profile.saving': 'Wird gespeichert...',
    'profile.dangerZone': 'Gefahrenzone',
    'profile.dangerZoneDesc': 'Unumkehrbare Aktionen für Ihr Konto',
    'profile.deleteAccount': 'Konto löschen',
    'profile.deleteAccountWarning': 'Sobald Sie Ihr Konto löschen, werden alle Ihre Daten dauerhaft entfernt. Diese Aktion kann nicht rückgängig gemacht werden.',
    'profile.deletingAccount': 'Wird gelöscht...',
    'profile.deleteAccountConfirmTitle': 'Sind Sie absolut sicher?',
    'profile.deleteAccountConfirmDesc': 'Dies löscht dauerhaft Ihr Konto und alle zugehörigen Daten einschließlich gespeicherter Checklisten, Abonnements und Profilinformationen.',
    'profile.confirmDelete': 'Ja, Konto löschen',
    'profile.accountDeleted': 'Konto gelöscht',
    'profile.accountDeletedDesc': 'Ihr Konto und alle zugehörigen Daten wurden dauerhaft gelöscht.',
    'profile.deleteAccountError': 'Konto konnte nicht gelöscht werden. Bitte erneut versuchen.',

    // Saved
    'saved.title': 'Meine gespeicherten Checklisten',
    'saved.generateNew': 'Neue erstellen',
    'saved.noChecklists': 'Noch keine Checklisten',
    'saved.noChecklistsDesc': 'Erstellen Sie eine Checkliste und speichern Sie sie',
    'saved.generateFirst': 'Erste Checkliste erstellen',
    'saved.deleted': 'Checkliste gelöscht',
    'saved.refreshed': 'Aktualisiert',

    // Common
    'common.loading': 'Wird geladen...',
    'common.error': 'Fehler',
    'common.success': 'Erfolg',
    'common.save': 'Speichern',
    'common.cancel': 'Abbrechen',
    'common.delete': 'Löschen',
    'common.view': 'Ansehen',
    'common.loginToSave': 'Anmelden zum Speichern',
    'common.dismiss': 'Schließen',
    'checklist.copy': 'Kopieren',
    'checklist.print': 'Drucken',
    'checklist.addItem': 'Eintrag hinzufügen',
    'checklist.items': 'Einträge',

    // Footer
    'footer.tagline': 'Ihre Anlaufstelle für Tausende von KI-generierten Checklisten für jede Lebenssituation.',
    'footer.quickLinks': 'Schnellzugriff',
    'footer.allCategories': 'Alle Kategorien',
    'footer.generateChecklist': 'Checkliste erstellen',
    'footer.popularCategories': 'Beliebte Kategorien',
    'footer.copyright': '© {year} Nexihow. Alle Rechte vorbehalten.',
    'footer.homeLife': 'Haus & Leben',
    'footer.travel': 'Reisen',
    'footer.workBusiness': 'Arbeit & Geschäft',

    // 404 Page
    'notFound.title': '404',
    'notFound.message': 'Seite nicht gefunden',
    'notFound.returnHome': 'Zur Startseite',

    // Search Page
    'search.title': 'Suchergebnisse',
    'search.resultsFor': '{count} Ergebnis(se) für "{query}"',
    'search.noResults': 'Keine Ergebnisse gefunden',
    'search.noResultsDesc': 'Versuchen Sie einen anderen Suchbegriff oder durchsuchen Sie unsere Kategorien.',
    'search.browseCategories': 'Kategorien durchsuchen',
    'search.generateCustom': 'Eigene Checkliste erstellen',

    // Category Page
    'category.notFound': 'Kategorie nicht gefunden',
    'category.notFoundDesc': 'Die gesuchte Kategorie existiert nicht.',
    'category.browseAll': 'Alle Kategorien anzeigen',
    'category.checklistsAvailable': '{count} Checkliste(n) verfügbar',
    'category.noChecklists': 'Noch keine Checklisten in dieser Kategorie.',
    'category.generateOne': 'Eine mit KI erstellen',

    // Checklist Page
    'checklistPage.notFound': 'Checkliste nicht gefunden',
    'checklistPage.notFoundDesc': 'Die gesuchte Checkliste existiert nicht.',
    'checklistPage.browseAll': 'Alle Checklisten anzeigen',
    'checklistPage.sound': 'Ton',
    'checklistPage.soundOn': 'An',
    'checklistPage.soundOff': 'Aus',
    'checklistPage.ofCompleted': '{checked} von {total} erledigt',
    'checklistPage.due': 'Fällig: {date}',
    'checklistPage.congratulations': 'Herzlichen Glückwunsch!',
    'checklistPage.completedMessage': 'Sie haben diese Checkliste abgeschlossen!',
    'checklistPage.saveCopy': 'Kopie speichern',
    'checklistPage.shareAchievement': 'Erfolg teilen',
    'checklistPage.relatedChecklists': 'Ähnliche Checklisten',

    // Saved Checklist View
    'savedView.notFound': 'Checkliste nicht gefunden',
    'savedView.backToSaved': 'Zurück zu gespeicherten Checklisten',
    'savedView.savedOn': 'Gespeichert am {date}',

    // Breadcrumbs
    'breadcrumb.home': 'Startseite',
    'breadcrumb.categories': 'Kategorien',
    'breadcrumb.searchResults': 'Suchergebnisse',
    'breadcrumb.myChecklists': 'Meine Projekte',

    // FAQ
    'faq.whatIs.question': 'Was ist Nexihow?',
    'faq.whatIs.answer': 'Nexihow ist eine kostenlose Plattform mit Tausenden von KI-generierten Checklisten für jede Lebenssituation, von Umzug über Reisen bis hin zu Arbeit und Wellness.',
    'faq.howToCreate.question': 'Wie erstelle ich eine eigene Checkliste?',
    'faq.howToCreate.answer': 'Gehen Sie einfach zu unserer Generator-Seite, geben Sie Ihr Thema ein, und unsere KI erstellt in Sekunden eine maßgeschneiderte Checkliste für Sie.',
    'faq.isFree.question': 'Sind die Checklisten kostenlos?',
    'faq.isFree.answer': 'Ja! Der grundlegende Zugang zu Checklisten und deren Erstellung ist völlig kostenlos. Premium-Funktionen wie unbegrenztes Speichern und PDF-Export sind mit einem Abonnement verfügbar.',
    'faq.canSave.question': 'Kann ich meine Checklisten speichern und teilen?',
    'faq.canSave.answer': 'Ja, Sie können Checklisten in Ihrem Konto speichern, in die Zwischenablage kopieren, ausdrucken oder über soziale Medien und direkte Links teilen.',

    // Additional missing keys
    'saved.countSaved': '{count} Checkliste(n) gespeichert',
    'saved.moreItemsCount': '+{count} weitere Einträge',
    'profile.memberSince': 'Mitglied seit: {date}',
    'profile.characters': '{count}/{max} Zeichen',
    'profile.fileTypeError': 'Ungültiger Dateityp',
    'profile.fileTypeErrorDesc': 'Bitte laden Sie ein JPG, PNG, WebP oder GIF-Bild hoch.',
    'profile.fileSizeError': 'Datei zu groß',
    'profile.fileSizeErrorDesc': 'Bitte laden Sie ein Bild unter 5MB hoch.',
    'profile.fileFormatHint': 'JPG, PNG, WebP oder GIF. Max. 5MB.',
    'generate.enterItem': 'Eintrag eingeben...',
    'dashboard.days': 'Tage',
    'dashboard.overdueCount': '{count} überfällige Erinnerung(en)',
    'dashboard.moreRemindersCount': '+ {count} weitere Erinnerungen',
    'checklist.progress': 'Fortschritt',
    'locale.selectLanguage': 'Sprache',

    // Affiliate
    'affiliate.recommended': 'Empfohlene Produkte',
    'affiliate.disclosure': 'Wir erhalten möglicherweise eine Provision für Käufe über diese Links.',
    'affiliate.shopNow': 'Jetzt kaufen',
    'affiliate.sponsored': 'Gesponsert',
    'affiliate.essentials': 'Wichtige Artikel für Ihre Checkliste',
  },

  fr: {
    // Navigation
    'nav.categories': 'Catégories',
    'nav.generate': 'Générer',
    'nav.myChecklists': 'Mes Projets',
    'nav.dashboard': 'Tableau de bord',
    'nav.profile': 'Paramètres du profil',
    'nav.admin': 'Administration',
    'nav.signOut': 'Déconnexion',
    'nav.login': 'Connexion',
    'nav.search': 'Rechercher des listes...',
    'nav.theme': 'Thème',
    'nav.region': 'Région',
    'nav.home': 'Accueil',

    // Home Page
    'home.hero.badge': 'Listes alimentées par IA',
    'home.hero.title': '10 000+ listes pour',
    'home.hero.titleHighlight': 'chaque situation',
    'home.hero.subtitle': 'Du déménagement à la planification de mariage, trouvez la liste parfaite ou créez la vôtre avec l\'IA.',
    'home.hero.cta': 'Parcourir les catégories',
    'home.hero.ctaSecondary': 'Créer une liste personnalisée',
    'home.browseByCategory': 'Parcourir par catégorie',
    'home.browseByCategory.subtitle': 'Listes organisées par domaine de vie',
    'home.featured': 'Listes en vedette',
    'home.featured.subtitle': 'Listes populaires appréciées par nos utilisateurs',
    'home.trending': 'Tendances actuelles',
    'home.trending.subtitle': 'Ce que les gens cochent cette semaine',
    'home.viewAll': 'Voir tout',
    'home.faq.title': 'Questions fréquentes',
    'home.faq.subtitle': 'Tout ce que vous devez savoir sur Nexihow',
    'home.cta.title': 'Vous ne trouvez pas ce qu\'il vous faut ?',
    'home.cta.subtitle': 'Utilisez notre générateur IA pour créer une liste personnalisée en quelques secondes.',
    'home.cta.button': 'Générer votre liste',
    'home.generator.tryThese': 'Essayez ceux-ci',
    'home.generator.examples.moving': '🏠 Déménagement',
    'home.generator.examples.wedding': '💒 Mariage',
    'home.generator.examples.travel': '✈️ Voyage',
    'home.generator.examples.baby': '👶 Bébé',
    'home.generator.tip': 'Soyez précis pour de meilleurs résultats — délais, contraintes ou niveau d\'expérience',

    // Category Labels
    'category.label.home': 'Maison & Vie',
    'category.label.work': 'Travail & Affaires',
    'category.label.money': 'Argent & Juridique',
    'category.label.travel': 'Voyage',
    'category.label.relationships': 'Relations & Personnel',
    'category.label.school': 'École & Apprentissage',
    'category.label.pets': 'Animaux',
    'category.label.diy': 'Bricolage & Garage',
    'category.label.food': 'Cuisine & Alimentation',
    'category.label.tech': 'Tech & Digital',
    'category.label.health': 'Santé & Sécurité',

    // Category Descriptions
    'category.description.home': 'Tout pour votre espace de vie, du déménagement à l\'entretien',
    'category.description.work': 'Carrière, affaires et développement professionnel',
    'category.description.money': 'Planification financière, budget et questions juridiques',
    'category.description.travel': 'Listes de voyage, planification et essentiels',
    'category.description.relationships': 'Rencontres, famille, événements et relations personnelles',
    'category.description.school': 'Éducation, études et réussite académique',
    'category.description.pets': 'Soins des animaux, adoption et essentiels',
    'category.description.diy': 'Amélioration de la maison, outils et projets pratiques',
    'category.description.food': 'Cuisine, planification des repas et recettes',
    'category.description.tech': 'Vie numérique, gadgets et sécurité en ligne',
    'category.description.health': 'Bien-être, sécurité et préparation santé',

    // Categories
    'categories.title': 'Toutes les catégories',
    'categories.subtitle': 'Parcourez notre collection complète de listes. De la maison au travail, des voyages à la santé.',
    'categories.checklists': 'listes',

    // Generate
    'generate.badge': 'Générateur IA',
    'generate.title': 'Créez votre liste personnalisée',
    'generate.subtitle': 'Entrez un sujet et notre IA créera une liste complète adaptée à vos besoins.',
    'generate.inputLabel': 'Pour quoi avez-vous besoin d\'une liste ?',
    'generate.placeholder': 'ex. Organiser une fête d\'anniversaire, Lancer un podcast, Préparer un marathon...',
    'generate.button': 'Générer la liste',
    'generate.buttonLoggedOut': 'Connectez-vous pour générer',
    'generate.generating': 'Génération en cours...',
    'generate.loginRequired': 'Connexion requise',
    'generate.tips.title': 'Conseils pour de meilleurs résultats :',
    'generate.tips.1': 'Soyez précis sur votre objectif',
    'generate.tips.2': 'Incluez les contraintes ou exigences',
    'generate.tips.3': 'Mentionnez le délai si pertinent',
    'generate.tips.4': 'Ajoutez du contexte sur votre expérience',

    // Auth
    'auth.login': 'Connexion',
    'auth.signup': 'Inscription',
    'auth.welcomeBack': 'Bon retour',
    'auth.createAccount': 'Créer un compte',
    'auth.loginDesc': 'Connectez-vous pour accéder à vos listes',
    'auth.signupDesc': 'Inscrivez-vous pour sauvegarder vos listes',
    'auth.loggingIn': 'Connexion...',
    'auth.creatingAccount': 'Création du compte...',
    'auth.noAccount': 'Pas encore de compte ?',
    'auth.hasAccount': 'Déjà un compte ?',
    'auth.email': 'Email',
    'auth.password': 'Mot de passe',
    'auth.displayName': 'Nom d\'affichage (optionnel)',
    'auth.forgotPassword': 'Mot de passe oublié ?',
    'auth.resetPassword': 'Réinitialiser le mot de passe',
    'auth.resetPasswordDesc': 'Entrez votre email et nous vous enverrons un lien de réinitialisation.',
    'auth.sendResetEmail': 'Envoyer le lien',
    'auth.sendingResetEmail': 'Envoi...',
    'auth.resetEmailSent': 'Email envoyé',
    'auth.resetEmailSentDesc': 'Vérifiez votre email pour le lien de réinitialisation.',
    'auth.resetFailed': 'Échec de la réinitialisation',
    'auth.backToLogin': 'Retour à la connexion',
    'auth.setNewPassword': 'Définir un nouveau mot de passe',
    'auth.setNewPasswordDesc': 'Entrez votre nouveau mot de passe ci-dessous.',
    'auth.newPassword': 'Nouveau mot de passe',
    'auth.confirmPassword': 'Confirmer le mot de passe',
    'auth.passwordsDoNotMatch': 'Les mots de passe ne correspondent pas',
    'auth.updatePassword': 'Mettre à jour',
    'auth.updatingPassword': 'Mise à jour...',
    'auth.updatePasswordFailed': 'Échec de la mise à jour',
    'auth.passwordUpdated': 'Mot de passe mis à jour',
    'auth.passwordUpdatedDesc': 'Votre mot de passe a été mis à jour avec succès.',
    'auth.redirectingHome': 'Redirection vers la page d\'accueil...',
    'auth.resetLinkExpired': 'Lien expiré',
    'auth.resetLinkExpiredDesc': 'Veuillez demander un nouveau lien.',
    'auth.verifyEmailReminder': 'Veuillez vérifier votre adresse email pour accéder à toutes les fonctionnalités.',
    'auth.resendVerification': 'Renvoyer',
    'auth.verificationSent': 'Email envoyé',
    'auth.verificationSentDesc': 'Vérifiez votre boîte de réception.',
    'auth.resendFailed': 'Échec de l\'envoi',
    'auth.sending': 'Envoi...',
    'auth.passwordStrength': 'Force du mot de passe',
    'auth.strengthWeak': 'Faible',
    'auth.strengthMedium': 'Moyen',
    'auth.strengthStrong': 'Fort',
    'auth.passwordRequirements': 'Le mot de passe doit contenir :',
    'auth.req8chars': 'Au moins 8 caractères',
    'auth.reqUppercase': 'Une lettre majuscule',
    'auth.reqLowercase': 'Une lettre minuscule',
    'auth.reqNumber': 'Un chiffre',
    'auth.reqSpecial': 'Un caractère spécial',
    'auth.emailValid': 'Email valide',
    'auth.emailInvalid': 'Format d\'email invalide',
    'auth.passwordsMatch': 'Les mots de passe correspondent',
    'auth.passwordsNoMatch': 'Les mots de passe ne correspondent pas',

    // Dashboard
    'dashboard.title': 'Votre tableau de bord',
    'dashboard.subtitle': 'Suivez votre progression',
    'dashboard.dayStreak': 'Jours consécutifs',
    'dashboard.bestStreak': 'Meilleure série',
    'dashboard.itemsDone': 'Terminés',
    'dashboard.achievements': 'Succès',
    'dashboard.todayProgress': 'Progression du jour',
    'dashboard.dailyGoal': 'Objectif quotidien',
    'dashboard.goalReached': 'Objectif atteint !',
    'dashboard.upcomingReminders': 'Rappels à venir',
    'dashboard.noReminders': 'Aucun rappel',
    'dashboard.setReminders': 'Ajoutez des rappels aux éléments de liste',
    'dashboard.streakProgress': 'Progression de la série',
    'dashboard.today': 'Aujourd\'hui',
    'dashboard.tomorrow': 'Demain',

    // Profile
    'profile.title': 'Paramètres du profil',
    'profile.subtitle': 'Gérez vos informations de compte',
    'profile.picture': 'Photo de profil',
    'profile.pictureDesc': 'Téléchargez une image (max 5Mo)',
    'profile.uploadImage': 'Télécharger',
    'profile.uploading': 'Téléchargement...',
    'profile.accountInfo': 'Informations du compte',
    'profile.accountInfoDesc': 'Mettez à jour votre nom d\'affichage',
    'profile.displayName': 'Nom d\'affichage',
    'profile.emailAddress': 'Adresse email',
    'profile.emailNote': 'L\'email ne peut pas être modifié',
    'profile.saveChanges': 'Enregistrer',
    'profile.saving': 'Enregistrement...',
    'profile.dangerZone': 'Zone de danger',
    'profile.dangerZoneDesc': 'Actions irréversibles pour votre compte',
    'profile.deleteAccount': 'Supprimer le compte',
    'profile.deleteAccountWarning': 'Une fois votre compte supprimé, toutes vos données seront définitivement effacées. Cette action est irréversible.',
    'profile.deletingAccount': 'Suppression...',
    'profile.deleteAccountConfirmTitle': 'Êtes-vous absolument sûr ?',
    'profile.deleteAccountConfirmDesc': 'Cela supprimera définitivement votre compte et toutes les données associées, y compris les listes sauvegardées, les abonnements et les informations de profil.',
    'profile.confirmDelete': 'Oui, supprimer mon compte',
    'profile.accountDeleted': 'Compte supprimé',
    'profile.accountDeletedDesc': 'Votre compte et toutes les données associées ont été définitivement supprimés.',
    'profile.deleteAccountError': 'Échec de la suppression du compte. Veuillez réessayer.',

    // Saved
    'saved.title': 'Mes listes sauvegardées',
    'saved.generateNew': 'Nouvelle liste',
    'saved.noChecklists': 'Aucune liste sauvegardée',
    'saved.noChecklistsDesc': 'Créez une liste et sauvegardez-la',
    'saved.generateFirst': 'Créer votre première liste',
    'saved.deleted': 'Liste supprimée',
    'saved.refreshed': 'Actualisé',

    // Common
    'common.loading': 'Chargement...',
    'common.error': 'Erreur',
    'common.success': 'Succès',
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.delete': 'Supprimer',
    'common.view': 'Voir',
    'common.loginToSave': 'Connectez-vous pour sauvegarder',
    'common.dismiss': 'Fermer',
    'checklist.copy': 'Copier',
    'checklist.print': 'Imprimer',
    'checklist.addItem': 'Ajouter un élément',
    'checklist.items': 'éléments',

    // Footer
    'footer.tagline': 'Votre ressource incontournable pour des milliers de listes générées par IA pour chaque situation.',
    'footer.quickLinks': 'Liens rapides',
    'footer.allCategories': 'Toutes les catégories',
    'footer.generateChecklist': 'Générer une liste',
    'footer.popularCategories': 'Catégories populaires',
    'footer.copyright': '© {year} Nexihow. Tous droits réservés.',
    'footer.homeLife': 'Maison & Vie',
    'footer.travel': 'Voyage',
    'footer.workBusiness': 'Travail & Affaires',

    // 404 Page
    'notFound.title': '404',
    'notFound.message': 'Page non trouvée',
    'notFound.returnHome': "Retour à l'accueil",

    // Search Page
    'search.title': 'Résultats de recherche',
    'search.resultsFor': '{count} résultat(s) pour "{query}"',
    'search.noResults': 'Aucun résultat trouvé',
    'search.noResultsDesc': 'Essayez un autre terme ou parcourez nos catégories.',
    'search.browseCategories': 'Parcourir les catégories',
    'search.generateCustom': 'Créer une liste personnalisée',

    // Category Page
    'category.notFound': 'Catégorie non trouvée',
    'category.notFoundDesc': "La catégorie recherchée n'existe pas.",
    'category.browseAll': 'Voir toutes les catégories',
    'category.checklistsAvailable': '{count} liste(s) disponible(s)',
    'category.noChecklists': 'Pas encore de listes dans cette catégorie.',
    'category.generateOne': 'Créer une avec IA',

    // Checklist Page
    'checklistPage.notFound': 'Liste non trouvée',
    'checklistPage.notFoundDesc': "La liste recherchée n'existe pas.",
    'checklistPage.browseAll': 'Voir toutes les listes',
    'checklistPage.sound': 'Son',
    'checklistPage.soundOn': 'Activé',
    'checklistPage.soundOff': 'Désactivé',
    'checklistPage.ofCompleted': '{checked} sur {total} terminé(s)',
    'checklistPage.due': 'Échéance : {date}',
    'checklistPage.congratulations': 'Félicitations !',
    'checklistPage.completedMessage': 'Vous avez terminé cette liste !',
    'checklistPage.saveCopy': 'Sauvegarder une copie',
    'checklistPage.shareAchievement': 'Partager la réussite',
    'checklistPage.relatedChecklists': 'Listes similaires',

    // Saved Checklist View
    'savedView.notFound': 'Liste non trouvée',
    'savedView.backToSaved': 'Retour aux listes sauvegardées',
    'savedView.savedOn': 'Sauvegardée le {date}',

    // Breadcrumbs
    'breadcrumb.home': 'Accueil',
    'breadcrumb.categories': 'Catégories',
    'breadcrumb.searchResults': 'Résultats de recherche',
    'breadcrumb.myChecklists': 'Mes projets',

    // FAQ
    'faq.whatIs.question': "Qu'est-ce que Nexihow ?",
    'faq.whatIs.answer': "Nexihow est une plateforme gratuite proposant des milliers de listes générées par IA pour chaque situation, du déménagement aux voyages en passant par le travail et le bien-être.",
    'faq.howToCreate.question': 'Comment créer une liste personnalisée ?',
    'faq.howToCreate.answer': "Allez simplement sur notre page Générer, entrez votre sujet et notre IA créera une liste personnalisée adaptée à vos besoins en quelques secondes.",
    'faq.isFree.question': 'Les listes sont-elles gratuites ?',
    'faq.isFree.answer': "Oui ! L'accès de base aux listes et leur génération sont entièrement gratuits. Les fonctionnalités premium comme les sauvegardes illimitées et l'export PDF sont disponibles avec un abonnement.",
    'faq.canSave.question': 'Puis-je sauvegarder et partager mes listes ?',
    'faq.canSave.answer': 'Oui, vous pouvez sauvegarder vos listes dans votre compte, les copier, les imprimer ou les partager via les réseaux sociaux et des liens directs.',

    // Additional missing keys
    'saved.countSaved': '{count} liste(s) sauvegardée(s)',
    'saved.moreItemsCount': '+{count} éléments de plus',
    'profile.memberSince': 'Membre depuis : {date}',
    'profile.characters': '{count}/{max} caractères',
    'profile.fileTypeError': 'Type de fichier invalide',
    'profile.fileTypeErrorDesc': 'Veuillez télécharger une image JPG, PNG, WebP ou GIF.',
    'profile.fileSizeError': 'Fichier trop volumineux',
    'profile.fileSizeErrorDesc': 'Veuillez télécharger une image de moins de 5 Mo.',
    'profile.fileFormatHint': 'JPG, PNG, WebP ou GIF. Max 5 Mo.',
    'generate.enterItem': 'Saisir un élément...',
    'dashboard.days': 'jours',
    'dashboard.overdueCount': '{count} rappel(s) en retard',
    'dashboard.moreRemindersCount': '+ {count} autres rappels',
    'locale.selectLanguage': 'Langue',
  },

  es: {
    // Navigation
    'nav.categories': 'Categorías',
    'nav.generate': 'Generar',
    'nav.myChecklists': 'Mis Proyectos',
    'nav.dashboard': 'Panel',
    'nav.profile': 'Configuración del perfil',
    'nav.admin': 'Administración',
    'nav.signOut': 'Cerrar sesión',
    'nav.login': 'Iniciar sesión',
    'nav.search': 'Buscar listas...',
    'nav.theme': 'Tema',
    'nav.region': 'Región',
    'nav.home': 'Inicio',

    // Home Page
    'home.hero.badge': 'Listas con IA',
    'home.hero.title': '10.000+ listas para',
    'home.hero.titleHighlight': 'cada situación',
    'home.hero.subtitle': 'Desde mudanzas hasta planificación de bodas, encuentra la lista perfecta o crea la tuya con IA.',
    'home.hero.cta': 'Explorar categorías',
    'home.hero.ctaSecondary': 'Crear lista personalizada',
    'home.browseByCategory': 'Explorar por categoría',
    'home.browseByCategory.subtitle': 'Listas organizadas por área de vida',
    'home.featured': 'Listas destacadas',
    'home.featured.subtitle': 'Listas populares de nuestros usuarios',
    'home.trending': 'Tendencias',
    'home.trending.subtitle': 'Lo que la gente marca esta semana',
    'home.viewAll': 'Ver todo',
    'home.faq.title': 'Preguntas frecuentes',
    'home.faq.subtitle': 'Todo lo que necesitas saber sobre Nexihow',
    'home.cta.title': '¿No encuentras lo que buscas?',
    'home.cta.subtitle': 'Usa nuestro generador de IA para crear una lista personalizada en segundos.',
    'home.cta.button': 'Genera tu lista',
    'home.generator.tryThese': 'Prueba estos',
    'home.generator.examples.moving': '🏠 Mudanza',
    'home.generator.examples.wedding': '💒 Boda',
    'home.generator.examples.travel': '✈️ Viaje',
    'home.generator.examples.baby': '👶 Bebé',
    'home.generator.tip': 'Sé específico para mejores resultados — plazos, requisitos o nivel de experiencia',

    // Category Labels
    'category.label.home': 'Hogar & Vida',
    'category.label.work': 'Trabajo & Negocios',
    'category.label.money': 'Dinero & Legal',
    'category.label.travel': 'Viajes',
    'category.label.relationships': 'Relaciones & Personal',
    'category.label.school': 'Escuela & Aprendizaje',
    'category.label.pets': 'Mascotas & Animales',
    'category.label.diy': 'Bricolaje & Garaje',
    'category.label.food': 'Comida & Cocina',
    'category.label.tech': 'Tech & Digital',
    'category.label.health': 'Salud & Seguridad',

    // Category Descriptions
    'category.description.home': 'Todo para tu espacio, desde mudanzas hasta mantenimiento',
    'category.description.work': 'Carrera, negocios y desarrollo profesional',
    'category.description.money': 'Planificación financiera, presupuesto y asuntos legales',
    'category.description.travel': 'Listas de equipaje, planificación de viajes y esenciales',
    'category.description.relationships': 'Citas, familia, eventos y conexiones personales',
    'category.description.school': 'Educación, estudio y éxito académico',
    'category.description.pets': 'Cuidado de mascotas, adopción y esenciales',
    'category.description.diy': 'Mejoras del hogar, herramientas y proyectos prácticos',
    'category.description.food': 'Cocina, planificación de comidas y recetas',
    'category.description.tech': 'Vida digital, gadgets y seguridad online',
    'category.description.health': 'Bienestar, seguridad y preparación de salud',

    // Categories
    'categories.title': 'Todas las categorías',
    'categories.subtitle': 'Explora nuestra colección completa de listas. Desde hogar y trabajo hasta viajes y salud.',
    'categories.checklists': 'listas',

    // Generate
    'generate.badge': 'Generador con IA',
    'generate.title': 'Crea tu lista personalizada',
    'generate.subtitle': 'Ingresa cualquier tema y nuestra IA creará una lista completa para ti.',
    'generate.inputLabel': '¿Para qué necesitas una lista?',
    'generate.placeholder': 'ej. Planificar fiesta de cumpleaños, Iniciar un podcast, Preparar maratón...',
    'generate.button': 'Generar lista',
    'generate.buttonLoggedOut': 'Inicia sesión para generar',
    'generate.generating': 'Generando...',
    'generate.loginRequired': 'Inicio de sesión requerido',
    'generate.tips.title': 'Consejos para mejores resultados:',
    'generate.tips.1': 'Sé específico sobre tu objetivo',
    'generate.tips.2': 'Incluye restricciones o requisitos',
    'generate.tips.3': 'Menciona el plazo si es relevante',
    'generate.tips.4': 'Añade contexto sobre tu experiencia',

    // Auth
    'auth.login': 'Iniciar sesión',
    'auth.signup': 'Registrarse',
    'auth.welcomeBack': 'Bienvenido de vuelta',
    'auth.createAccount': 'Crear cuenta',
    'auth.loginDesc': 'Inicia sesión para acceder a tus listas',
    'auth.signupDesc': 'Regístrate para guardar tus listas',
    'auth.loggingIn': 'Iniciando sesión...',
    'auth.creatingAccount': 'Creando cuenta...',
    'auth.noAccount': '¿No tienes cuenta?',
    'auth.hasAccount': '¿Ya tienes cuenta?',
    'auth.email': 'Email',
    'auth.password': 'Contraseña',
    'auth.displayName': 'Nombre (opcional)',
    'auth.forgotPassword': '¿Olvidaste tu contraseña?',
    'auth.resetPassword': 'Restablecer contraseña',
    'auth.resetPasswordDesc': 'Ingresa tu email y te enviaremos un enlace para restablecer.',
    'auth.sendResetEmail': 'Enviar enlace',
    'auth.sendingResetEmail': 'Enviando...',
    'auth.resetEmailSent': 'Email enviado',
    'auth.resetEmailSentDesc': 'Revisa tu email para el enlace de restablecimiento.',
    'auth.resetFailed': 'Error al restablecer',
    'auth.backToLogin': 'Volver al inicio de sesión',
    'auth.setNewPassword': 'Establecer nueva contraseña',
    'auth.setNewPasswordDesc': 'Ingresa tu nueva contraseña abajo.',
    'auth.newPassword': 'Nueva contraseña',
    'auth.confirmPassword': 'Confirmar contraseña',
    'auth.passwordsDoNotMatch': 'Las contraseñas no coinciden',
    'auth.updatePassword': 'Actualizar contraseña',
    'auth.updatingPassword': 'Actualizando...',
    'auth.updatePasswordFailed': 'Error al actualizar',
    'auth.passwordUpdated': 'Contraseña actualizada',
    'auth.passwordUpdatedDesc': 'Tu contraseña ha sido actualizada correctamente.',
    'auth.redirectingHome': 'Redirigiendo a la página principal...',
    'auth.resetLinkExpired': 'Enlace expirado',
    'auth.resetLinkExpiredDesc': 'Por favor solicita un nuevo enlace.',
    'auth.verifyEmailReminder': 'Por favor verifica tu email para acceder a todas las funciones.',
    'auth.resendVerification': 'Reenviar',
    'auth.verificationSent': 'Email enviado',
    'auth.verificationSentDesc': 'Revisa tu bandeja de entrada.',
    'auth.resendFailed': 'Error al enviar',
    'auth.sending': 'Enviando...',
    'auth.passwordStrength': 'Seguridad de la contraseña',
    'auth.strengthWeak': 'Débil',
    'auth.strengthMedium': 'Media',
    'auth.strengthStrong': 'Fuerte',
    'auth.passwordRequirements': 'La contraseña debe contener:',
    'auth.req8chars': 'Al menos 8 caracteres',
    'auth.reqUppercase': 'Una letra mayúscula',
    'auth.reqLowercase': 'Una letra minúscula',
    'auth.reqNumber': 'Un número',
    'auth.reqSpecial': 'Un carácter especial',
    'auth.emailValid': 'Email válido',
    'auth.emailInvalid': 'Formato de email inválido',
    'auth.passwordsMatch': 'Las contraseñas coinciden',
    'auth.passwordsNoMatch': 'Las contraseñas no coinciden',

    // Dashboard
    'dashboard.title': 'Tu panel',
    'dashboard.subtitle': 'Sigue tu progreso',
    'dashboard.dayStreak': 'Racha de días',
    'dashboard.bestStreak': 'Mejor racha',
    'dashboard.itemsDone': 'Completados',
    'dashboard.achievements': 'Logros',
    'dashboard.todayProgress': 'Progreso de hoy',
    'dashboard.dailyGoal': 'Meta diaria',
    'dashboard.goalReached': '¡Meta alcanzada!',
    'dashboard.upcomingReminders': 'Recordatorios',
    'dashboard.noReminders': 'Sin recordatorios',
    'dashboard.setReminders': 'Añade recordatorios a elementos de lista',
    'dashboard.streakProgress': 'Progreso de racha',
    'dashboard.today': 'Hoy',
    'dashboard.tomorrow': 'Mañana',

    // Profile
    'profile.title': 'Configuración del perfil',
    'profile.subtitle': 'Gestiona tu información de cuenta',
    'profile.picture': 'Foto de perfil',
    'profile.pictureDesc': 'Sube una imagen (máx 5MB)',
    'profile.uploadImage': 'Subir imagen',
    'profile.uploading': 'Subiendo...',
    'profile.accountInfo': 'Información de cuenta',
    'profile.accountInfoDesc': 'Actualiza tu nombre',
    'profile.displayName': 'Nombre',
    'profile.emailAddress': 'Email',
    'profile.emailNote': 'El email no se puede cambiar',
    'profile.saveChanges': 'Guardar cambios',
    'profile.saving': 'Guardando...',
    'profile.dangerZone': 'Zona de peligro',
    'profile.dangerZoneDesc': 'Acciones irreversibles para tu cuenta',
    'profile.deleteAccount': 'Eliminar cuenta',
    'profile.deleteAccountWarning': 'Una vez que elimines tu cuenta, todos tus datos serán eliminados permanentemente. Esta acción no se puede deshacer.',
    'profile.deletingAccount': 'Eliminando...',
    'profile.deleteAccountConfirmTitle': '¿Estás absolutamente seguro?',
    'profile.deleteAccountConfirmDesc': 'Esto eliminará permanentemente tu cuenta y todos los datos asociados, incluidas las listas guardadas, suscripciones e información del perfil.',
    'profile.confirmDelete': 'Sí, eliminar mi cuenta',
    'profile.accountDeleted': 'Cuenta eliminada',
    'profile.accountDeletedDesc': 'Tu cuenta y todos los datos asociados han sido eliminados permanentemente.',
    'profile.deleteAccountError': 'Error al eliminar la cuenta. Por favor, inténtalo de nuevo.',

    // Saved
    'saved.title': 'Mis listas guardadas',
    'saved.generateNew': 'Nueva lista',
    'saved.noChecklists': 'Sin listas guardadas',
    'saved.noChecklistsDesc': 'Crea una lista y guárdala',
    'saved.generateFirst': 'Crea tu primera lista',
    'saved.deleted': 'Lista eliminada',
    'saved.refreshed': 'Actualizado',

    // Common
    'common.loading': 'Cargando...',
    'common.error': 'Error',
    'common.success': 'Éxito',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.view': 'Ver',
    'common.loginToSave': 'Inicia sesión para guardar',
    'common.dismiss': 'Cerrar',
    'checklist.copy': 'Copiar',
    'checklist.print': 'Imprimir',
    'checklist.addItem': 'Añadir elemento',
    'checklist.items': 'elementos',

    // Footer
    'footer.tagline': 'Tu recurso para miles de listas generadas por IA para cada situación de la vida.',
    'footer.quickLinks': 'Enlaces rápidos',
    'footer.allCategories': 'Todas las categorías',
    'footer.generateChecklist': 'Generar lista',
    'footer.popularCategories': 'Categorías populares',
    'footer.copyright': '© {year} Nexihow. Todos los derechos reservados.',
    'footer.homeLife': 'Hogar & Vida',
    'footer.travel': 'Viajes',
    'footer.workBusiness': 'Trabajo & Negocios',

    // 404 Page
    'notFound.title': '404',
    'notFound.message': '¡Página no encontrada!',
    'notFound.returnHome': 'Volver al inicio',

    // Search Page
    'search.title': 'Resultados de búsqueda',
    'search.resultsFor': '{count} resultado(s) para "{query}"',
    'search.noResults': 'No se encontraron resultados',
    'search.noResultsDesc': 'Prueba con otro término o explora nuestras categorías.',
    'search.browseCategories': 'Explorar categorías',
    'search.generateCustom': 'Crear lista personalizada',

    // Category Page
    'category.notFound': 'Categoría no encontrada',
    'category.notFoundDesc': 'La categoría que buscas no existe.',
    'category.browseAll': 'Ver todas las categorías',
    'category.checklistsAvailable': '{count} lista(s) disponible(s)',
    'category.noChecklists': 'Aún no hay listas en esta categoría.',
    'category.generateOne': 'Crear una con IA',

    // Checklist Page
    'checklistPage.notFound': 'Lista no encontrada',
    'checklistPage.notFoundDesc': 'La lista que buscas no existe.',
    'checklistPage.browseAll': 'Ver todas las listas',
    'checklistPage.sound': 'Sonido',
    'checklistPage.soundOn': 'Activado',
    'checklistPage.soundOff': 'Desactivado',
    'checklistPage.ofCompleted': '{checked} de {total} completado(s)',
    'checklistPage.due': 'Vence: {date}',
    'checklistPage.congratulations': '¡Felicidades!',
    'checklistPage.completedMessage': '¡Has completado esta lista!',
    'checklistPage.saveCopy': 'Guardar copia',
    'checklistPage.shareAchievement': 'Compartir logro',
    'checklistPage.relatedChecklists': 'Listas relacionadas',

    // Saved Checklist View
    'savedView.notFound': 'Lista no encontrada',
    'savedView.backToSaved': 'Volver a listas guardadas',
    'savedView.savedOn': 'Guardada el {date}',

    // Breadcrumbs
    'breadcrumb.home': 'Inicio',
    'breadcrumb.categories': 'Categorías',
    'breadcrumb.searchResults': 'Resultados de búsqueda',
    'breadcrumb.myChecklists': 'Mis proyectos',

    // FAQ
    'faq.whatIs.question': '¿Qué es ChecklistHQ?',
    'faq.whatIs.answer': 'ChecklistHQ es una plataforma gratuita que ofrece miles de listas generadas por IA para cada situación de la vida, desde mudanzas y viajes hasta trabajo y bienestar.',
    'faq.howToCreate.question': '¿Cómo creo una lista personalizada?',
    'faq.howToCreate.answer': 'Simplemente ve a nuestra página Generar, ingresa tu tema y nuestra IA creará una lista personalizada adaptada a tus necesidades en segundos.',
    'faq.isFree.question': '¿Las listas son gratuitas?',
    'faq.isFree.answer': '¡Sí! El acceso básico a listas y su generación es completamente gratis. Las funciones premium como guardados ilimitados y exportación PDF están disponibles con suscripción.',
    'faq.canSave.question': '¿Puedo guardar y compartir mis listas?',
    'faq.canSave.answer': 'Sí, puedes guardar listas en tu cuenta, copiarlas, imprimirlas o compartirlas a través de redes sociales y enlaces directos.',

    // Additional missing keys
    'saved.countSaved': '{count} lista(s) guardada(s)',
    'saved.moreItemsCount': '+{count} elementos más',
    'profile.memberSince': 'Miembro desde: {date}',
    'profile.characters': '{count}/{max} caracteres',
    'profile.fileTypeError': 'Tipo de archivo inválido',
    'profile.fileTypeErrorDesc': 'Por favor sube una imagen JPG, PNG, WebP o GIF.',
    'profile.fileSizeError': 'Archivo demasiado grande',
    'profile.fileSizeErrorDesc': 'Por favor sube una imagen menor a 5MB.',
    'profile.fileFormatHint': 'JPG, PNG, WebP o GIF. Máx 5MB.',
    'generate.enterItem': 'Ingresar elemento...',
    'dashboard.days': 'días',
    'dashboard.overdueCount': '{count} recordatorio(s) vencido(s)',
    'dashboard.moreRemindersCount': '+ {count} recordatorios más',
    'locale.selectLanguage': 'Idioma',
  },

  pt: {
    // Navigation
    'nav.categories': 'Categorias',
    'nav.generate': 'Gerar',
    'nav.myChecklists': 'Minhas Listas',
    'nav.dashboard': 'Painel',
    'nav.profile': 'Configurações do perfil',
    'nav.admin': 'Administração',
    'nav.signOut': 'Sair',
    'nav.login': 'Entrar',
    'nav.search': 'Pesquisar listas...',
    'nav.theme': 'Tema',
    'nav.region': 'Região',
    'nav.home': 'Início',

    // Home Page
    'home.hero.badge': 'Listas com IA',
    'home.hero.title': '10.000+ listas para',
    'home.hero.titleHighlight': 'cada situação',
    'home.hero.subtitle': 'De mudanças a planejamento de casamentos, encontre a lista perfeita ou crie a sua com IA.',
    'home.hero.cta': 'Explorar categorias',
    'home.hero.ctaSecondary': 'Criar lista personalizada',
    'home.browseByCategory': 'Explorar por categoria',
    'home.browseByCategory.subtitle': 'Listas organizadas por área da vida',
    'home.featured': 'Listas em destaque',
    'home.featured.subtitle': 'Listas populares dos nossos usuários',
    'home.trending': 'Em alta',
    'home.trending.subtitle': 'O que as pessoas estão marcando esta semana',
    'home.viewAll': 'Ver tudo',
    'home.faq.title': 'Perguntas frequentes',
    'home.faq.subtitle': 'Tudo o que você precisa saber sobre o Nexihow',
    'home.cta.title': 'Não encontrou o que precisa?',
    'home.cta.subtitle': 'Use nosso gerador de IA para criar uma lista personalizada em segundos.',
    'home.cta.button': 'Gerar sua lista',
    'home.generator.tryThese': 'Experimente estes',
    'home.generator.examples.moving': '🏠 Mudança',
    'home.generator.examples.wedding': '💒 Casamento',
    'home.generator.examples.travel': '✈️ Viagem',
    'home.generator.examples.baby': '👶 Bebê',
    'home.generator.tip': 'Seja específico para melhores resultados — prazos, requisitos ou nível de experiência',

    // Category Labels
    'category.label.home': 'Casa & Vida',
    'category.label.work': 'Trabalho & Negócios',
    'category.label.money': 'Dinheiro & Jurídico',
    'category.label.travel': 'Viagens',
    'category.label.relationships': 'Relacionamentos & Pessoal',
    'category.label.school': 'Escola & Aprendizado',
    'category.label.pets': 'Pets & Animais',
    'category.label.diy': 'Faça Você Mesmo',
    'category.label.food': 'Comida & Cozinha',
    'category.label.tech': 'Tech & Digital',
    'category.label.health': 'Saúde & Segurança',

    // Category Descriptions
    'category.description.home': 'Tudo para seu espaço, de mudanças à manutenção',
    'category.description.work': 'Carreira, negócios e desenvolvimento profissional',
    'category.description.money': 'Planejamento financeiro, orçamento e questões legais',
    'category.description.travel': 'Listas de viagem, planejamento e essenciais',
    'category.description.relationships': 'Encontros, família, eventos e conexões pessoais',
    'category.description.school': 'Educação, estudo e sucesso acadêmico',
    'category.description.pets': 'Cuidados com pets, adoção e essenciais',
    'category.description.diy': 'Melhorias em casa, ferramentas e projetos práticos',
    'category.description.food': 'Cozinha, planejamento de refeições e culinária',
    'category.description.tech': 'Vida digital, gadgets e segurança online',
    'category.description.health': 'Bem-estar, segurança e preparação de saúde',

    // Categories
    'categories.title': 'Todas as categorias',
    'categories.subtitle': 'Explore nossa coleção completa de listas. De casa e trabalho a viagens e saúde.',
    'categories.checklists': 'listas',

    // Generate
    'generate.badge': 'Gerador com IA',
    'generate.title': 'Crie sua lista personalizada',
    'generate.subtitle': 'Digite qualquer tema e nossa IA criará uma lista completa para você.',
    'generate.inputLabel': 'Para que você precisa de uma lista?',
    'generate.placeholder': 'ex. Planejar festa de aniversário, Começar um podcast, Preparar para maratona...',
    'generate.button': 'Gerar lista',
    'generate.buttonLoggedOut': 'Entre para gerar',
    'generate.generating': 'Gerando...',
    'generate.loginRequired': 'Login necessário',
    'generate.tips.title': 'Dicas para melhores resultados:',
    'generate.tips.1': 'Seja específico sobre seu objetivo',
    'generate.tips.2': 'Inclua restrições ou requisitos',
    'generate.tips.3': 'Mencione o prazo se relevante',
    'generate.tips.4': 'Adicione contexto sobre sua experiência',

    // Auth
    'auth.login': 'Entrar',
    'auth.signup': 'Cadastrar',
    'auth.welcomeBack': 'Bem-vindo de volta',
    'auth.createAccount': 'Criar conta',
    'auth.loginDesc': 'Entre para acessar suas listas',
    'auth.signupDesc': 'Cadastre-se para salvar suas listas',
    'auth.loggingIn': 'Entrando...',
    'auth.creatingAccount': 'Criando conta...',
    'auth.noAccount': 'Não tem conta?',
    'auth.hasAccount': 'Já tem conta?',
    'auth.email': 'Email',
    'auth.password': 'Senha',
    'auth.displayName': 'Nome (opcional)',
    'auth.forgotPassword': 'Esqueceu a senha?',
    'auth.resetPassword': 'Redefinir senha',
    'auth.resetPasswordDesc': 'Digite seu email e enviaremos um link para redefinir.',
    'auth.sendResetEmail': 'Enviar link',
    'auth.sendingResetEmail': 'Enviando...',
    'auth.resetEmailSent': 'Email enviado',
    'auth.resetEmailSentDesc': 'Verifique seu email para o link de redefinição.',
    'auth.resetFailed': 'Falha ao redefinir',
    'auth.backToLogin': 'Voltar ao login',
    'auth.setNewPassword': 'Definir nova senha',
    'auth.setNewPasswordDesc': 'Digite sua nova senha abaixo.',
    'auth.newPassword': 'Nova senha',
    'auth.confirmPassword': 'Confirmar senha',
    'auth.passwordsDoNotMatch': 'As senhas não coincidem',
    'auth.updatePassword': 'Atualizar senha',
    'auth.updatingPassword': 'Atualizando...',
    'auth.updatePasswordFailed': 'Falha ao atualizar',
    'auth.passwordUpdated': 'Senha atualizada',
    'auth.passwordUpdatedDesc': 'Sua senha foi atualizada com sucesso.',
    'auth.redirectingHome': 'Redirecionando para a página inicial...',
    'auth.resetLinkExpired': 'Link expirado',
    'auth.resetLinkExpiredDesc': 'Por favor, solicite um novo link.',
    'auth.verifyEmailReminder': 'Por favor, verifique seu email para acessar todos os recursos.',
    'auth.resendVerification': 'Reenviar',
    'auth.verificationSent': 'Email enviado',
    'auth.verificationSentDesc': 'Verifique sua caixa de entrada.',
    'auth.resendFailed': 'Falha ao enviar',
    'auth.sending': 'Enviando...',
    'auth.passwordStrength': 'Força da senha',
    'auth.strengthWeak': 'Fraca',
    'auth.strengthMedium': 'Média',
    'auth.strengthStrong': 'Forte',
    'auth.passwordRequirements': 'A senha deve conter:',
    'auth.req8chars': 'Pelo menos 8 caracteres',
    'auth.reqUppercase': 'Uma letra maiúscula',
    'auth.reqLowercase': 'Uma letra minúscula',
    'auth.reqNumber': 'Um número',
    'auth.reqSpecial': 'Um caractere especial',
    'auth.emailValid': 'Email válido',
    'auth.emailInvalid': 'Formato de email inválido',
    'auth.passwordsMatch': 'As senhas coincidem',
    'auth.passwordsNoMatch': 'As senhas não coincidem',

    // Dashboard
    'dashboard.title': 'Seu painel',
    'dashboard.subtitle': 'Acompanhe seu progresso',
    'dashboard.dayStreak': 'Sequência de dias',
    'dashboard.bestStreak': 'Melhor sequência',
    'dashboard.itemsDone': 'Concluídos',
    'dashboard.achievements': 'Conquistas',
    'dashboard.todayProgress': 'Progresso de hoje',
    'dashboard.dailyGoal': 'Meta diária',
    'dashboard.goalReached': 'Meta alcançada!',
    'dashboard.upcomingReminders': 'Lembretes',
    'dashboard.noReminders': 'Sem lembretes',
    'dashboard.setReminders': 'Adicione lembretes aos itens da lista',
    'dashboard.streakProgress': 'Progresso da sequência',
    'dashboard.today': 'Hoje',
    'dashboard.tomorrow': 'Amanhã',

    // Profile
    'profile.title': 'Configurações do perfil',
    'profile.subtitle': 'Gerencie suas informações de conta',
    'profile.picture': 'Foto do perfil',
    'profile.pictureDesc': 'Envie uma imagem (máx 5MB)',
    'profile.uploadImage': 'Enviar imagem',
    'profile.uploading': 'Enviando...',
    'profile.accountInfo': 'Informações da conta',
    'profile.accountInfoDesc': 'Atualize seu nome',
    'profile.displayName': 'Nome',
    'profile.emailAddress': 'Email',
    'profile.emailNote': 'O email não pode ser alterado',
    'profile.saveChanges': 'Salvar alterações',
    'profile.saving': 'Salvando...',
    'profile.dangerZone': 'Zona de perigo',
    'profile.dangerZoneDesc': 'Ações irreversíveis para sua conta',
    'profile.deleteAccount': 'Excluir conta',
    'profile.deleteAccountWarning': 'Depois de excluir sua conta, todos os seus dados serão removidos permanentemente. Esta ação não pode ser desfeita.',
    'profile.deletingAccount': 'Excluindo...',
    'profile.deleteAccountConfirmTitle': 'Você tem certeza absoluta?',
    'profile.deleteAccountConfirmDesc': 'Isso excluirá permanentemente sua conta e todos os dados associados, incluindo listas salvas, assinaturas e informações do perfil.',
    'profile.confirmDelete': 'Sim, excluir minha conta',
    'profile.accountDeleted': 'Conta excluída',
    'profile.accountDeletedDesc': 'Sua conta e todos os dados associados foram excluídos permanentemente.',
    'profile.deleteAccountError': 'Falha ao excluir a conta. Por favor, tente novamente.',

    // Saved
    'saved.title': 'Minhas listas salvas',
    'saved.generateNew': 'Nova lista',
    'saved.noChecklists': 'Sem listas salvas',
    'saved.noChecklistsDesc': 'Crie uma lista e salve-a',
    'saved.generateFirst': 'Crie sua primeira lista',
    'saved.deleted': 'Lista excluída',
    'saved.refreshed': 'Atualizado',

    // Common
    'common.loading': 'Carregando...',
    'common.error': 'Erro',
    'common.success': 'Sucesso',
    'common.save': 'Salvar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Excluir',
    'common.view': 'Ver',
    'common.loginToSave': 'Entre para salvar',
    'common.dismiss': 'Fechar',
    'checklist.copy': 'Copiar',
    'checklist.print': 'Imprimir',
    'checklist.addItem': 'Adicionar item',
    'checklist.items': 'itens',

    // Footer
    'footer.tagline': 'Seu recurso para milhares de checklists geradas por IA para cada situação da vida.',
    'footer.quickLinks': 'Links rápidos',
    'footer.allCategories': 'Todas as categorias',
    'footer.generateChecklist': 'Gerar checklist',
    'footer.popularCategories': 'Categorias populares',
    'footer.copyright': '© {year} Nexihow. Todos os direitos reservados.',
    'footer.homeLife': 'Casa & Vida',
    'footer.travel': 'Viagem',
    'footer.workBusiness': 'Trabalho & Negócios',

    // 404 Page
    'notFound.title': '404',
    'notFound.message': 'Página não encontrada',
    'notFound.returnHome': 'Voltar ao início',

    // Search Page
    'search.title': 'Resultados da pesquisa',
    'search.resultsFor': '{count} resultado(s) para "{query}"',
    'search.noResults': 'Nenhum resultado encontrado',
    'search.noResultsDesc': 'Tente outro termo ou explore nossas categorias.',
    'search.browseCategories': 'Explorar categorias',
    'search.generateCustom': 'Criar lista personalizada',

    // Category Page
    'category.notFound': 'Categoria não encontrada',
    'category.notFoundDesc': 'A categoria que você procura não existe.',
    'category.browseAll': 'Ver todas as categorias',
    'category.checklistsAvailable': '{count} lista(s) disponível(is)',
    'category.noChecklists': 'Ainda não há listas nesta categoria.',
    'category.generateOne': 'Criar uma com IA',

    // Checklist Page
    'checklistPage.notFound': 'Lista não encontrada',
    'checklistPage.notFoundDesc': 'A lista que você procura não existe.',
    'checklistPage.browseAll': 'Ver todas as listas',
    'checklistPage.sound': 'Som',
    'checklistPage.soundOn': 'Ligado',
    'checklistPage.soundOff': 'Desligado',
    'checklistPage.ofCompleted': '{checked} de {total} concluído(s)',
    'checklistPage.due': 'Vencimento: {date}',
    'checklistPage.congratulations': 'Parabéns!',
    'checklistPage.completedMessage': 'Você completou esta lista!',
    'checklistPage.saveCopy': 'Salvar cópia',
    'checklistPage.shareAchievement': 'Compartilhar conquista',
    'checklistPage.relatedChecklists': 'Listas relacionadas',

    // Saved Checklist View
    'savedView.notFound': 'Lista não encontrada',
    'savedView.backToSaved': 'Voltar às listas salvas',
    'savedView.savedOn': 'Salva em {date}',

    // Breadcrumbs
    'breadcrumb.home': 'Início',
    'breadcrumb.categories': 'Categorias',
    'breadcrumb.searchResults': 'Resultados da pesquisa',
    'breadcrumb.myChecklists': 'Minhas listas',

    // FAQ
    'faq.whatIs.question': 'O que é o Nexihow?',
    'faq.whatIs.answer': 'Nexihow é uma plataforma gratuita que oferece milhares de listas geradas por IA para cada situação da vida, de mudanças e viagens a trabalho e bem-estar.',
    'faq.howToCreate.question': 'Como crio uma lista personalizada?',
    'faq.howToCreate.answer': 'Basta ir para nossa página Gerar, inserir seu tema e nossa IA criará uma lista personalizada adaptada às suas necessidades em segundos.',
    'faq.isFree.question': 'As listas são gratuitas?',
    'faq.isFree.answer': 'Sim! O acesso básico às listas e sua geração é completamente gratuito. Recursos premium como salvamentos ilimitados e exportação PDF estão disponíveis com assinatura.',
    'faq.canSave.question': 'Posso salvar e compartilhar minhas listas?',
    'faq.canSave.answer': 'Sim, você pode salvar listas na sua conta, copiá-las, imprimi-las ou compartilhá-las através de redes sociais e links diretos.',

    // Additional missing keys
    'saved.countSaved': '{count} lista(s) salva(s)',
    'saved.moreItemsCount': '+{count} itens a mais',
    'profile.memberSince': 'Membro desde: {date}',
    'profile.characters': '{count}/{max} caracteres',
    'profile.fileTypeError': 'Tipo de arquivo inválido',
    'profile.fileTypeErrorDesc': 'Por favor, envie uma imagem JPG, PNG, WebP ou GIF.',
    'profile.fileSizeError': 'Arquivo muito grande',
    'profile.fileSizeErrorDesc': 'Por favor, envie uma imagem menor que 5MB.',
    'profile.fileFormatHint': 'JPG, PNG, WebP ou GIF. Máx 5MB.',
    'generate.enterItem': 'Inserir item...',
    'dashboard.days': 'dias',
    'dashboard.overdueCount': '{count} lembrete(s) atrasado(s)',
    'dashboard.moreRemindersCount': '+ {count} lembretes a mais',
    'locale.selectLanguage': 'Idioma',
  },

  ja: {
    // Navigation
    'nav.categories': 'カテゴリー',
    'nav.generate': '作成',
    'nav.myChecklists': 'マイリスト',
    'nav.dashboard': 'ダッシュボード',
    'nav.profile': 'プロフィール設定',
    'nav.admin': '管理画面',
    'nav.signOut': 'ログアウト',
    'nav.login': 'ログイン',
    'nav.search': 'チェックリストを検索...',
    'nav.theme': 'テーマ',
    'nav.region': '地域',
    'nav.home': 'ホーム',

    // Home Page
    'home.hero.badge': 'AI搭載チェックリスト',
    'home.hero.title': '10,000以上のリスト',
    'home.hero.titleHighlight': 'あらゆる状況に',
    'home.hero.subtitle': '引っ越しから結婚式の計画まで、完璧なチェックリストを見つけるか、AIで独自のリストを作成しましょう。',
    'home.hero.cta': 'カテゴリーを見る',
    'home.hero.ctaSecondary': 'カスタムリストを作成',
    'home.browseByCategory': 'カテゴリー別に探す',
    'home.browseByCategory.subtitle': '生活分野別に整理されたリスト',
    'home.featured': 'おすすめリスト',
    'home.featured.subtitle': 'ユーザーに人気のリスト',
    'home.trending': 'トレンド',
    'home.trending.subtitle': '今週チェックされているもの',
    'home.viewAll': 'すべて見る',
    'home.faq.title': 'よくある質問',
    'home.faq.subtitle': 'Nexihowについて知っておくべきこと',
    'home.cta.title': 'お探しのものが見つかりませんか？',
    'home.cta.subtitle': 'AIジェネレーターを使用して、数秒でカスタムリストを作成しましょう。',
    'home.cta.button': 'リストを作成',
    'home.generator.tryThese': 'これらを試す',
    'home.generator.examples.moving': '🏠 引っ越し',
    'home.generator.examples.wedding': '💒 結婚式',
    'home.generator.examples.travel': '✈️ 旅行',
    'home.generator.examples.baby': '👶 赤ちゃん',
    'home.generator.tip': '具体的に入力すると良い結果が得られます — 期限、条件、経験レベルを含めてください',

    // Category Labels
    'category.label.home': '家庭 & 生活',
    'category.label.work': '仕事 & ビジネス',
    'category.label.money': 'お金 & 法律',
    'category.label.travel': '旅行',
    'category.label.relationships': '人間関係 & パーソナル',
    'category.label.school': '学校 & 学習',
    'category.label.pets': 'ペット & 動物',
    'category.label.diy': 'DIY & ガレージ',
    'category.label.food': '食べ物 & キッチン',
    'category.label.tech': 'テック & デジタル',
    'category.label.health': '健康 & 安全',

    // Category Descriptions
    'category.description.home': '引っ越しからメンテナンスまで、住まいのすべて',
    'category.description.work': 'キャリア、ビジネス、専門的な成長',
    'category.description.money': '財務計画、予算、法的事項',
    'category.description.travel': 'パッキングリスト、旅行計画、必需品',
    'category.description.relationships': 'デート、家族、イベント、人間関係',
    'category.description.school': '教育、勉強、学業の成功',
    'category.description.pets': 'ペットケア、譲渡、必需品',
    'category.description.diy': 'ホームセンター、工具、実践プロジェクト',
    'category.description.food': 'キッチン設備、献立、料理',
    'category.description.tech': 'デジタルライフ、ガジェット、オンラインセキュリティ',
    'category.description.health': 'ウェルネス、安全、健康準備',

    // Categories
    'categories.title': 'すべてのカテゴリー',
    'categories.subtitle': 'リストの完全なコレクションをご覧ください。家庭から仕事、旅行から健康まで。',
    'categories.checklists': 'リスト',

    // Generate
    'generate.badge': 'AIジェネレーター',
    'generate.title': 'カスタムリストを作成',
    'generate.subtitle': 'トピックを入力すると、AIがあなたのニーズに合ったリストを作成します。',
    'generate.inputLabel': '何のリストが必要ですか？',
    'generate.placeholder': '例：誕生日パーティーの計画、ポッドキャストの開始、マラソンの準備...',
    'generate.button': 'リストを作成',
    'generate.buttonLoggedOut': 'ログインして作成',
    'generate.generating': '作成中...',
    'generate.loginRequired': 'ログインが必要です',
    'generate.tips.title': 'より良い結果のためのヒント：',
    'generate.tips.1': '目標を具体的に',
    'generate.tips.2': '制約や要件を含める',
    'generate.tips.3': '関連する場合は期間を記載',
    'generate.tips.4': '経験レベルについてのコンテキストを追加',

    // Auth
    'auth.login': 'ログイン',
    'auth.signup': '新規登録',
    'auth.welcomeBack': 'おかえりなさい',
    'auth.createAccount': 'アカウント作成',
    'auth.loginDesc': 'リストにアクセスするにはログインしてください',
    'auth.signupDesc': 'リストを保存するには登録してください',
    'auth.loggingIn': 'ログイン中...',
    'auth.creatingAccount': 'アカウント作成中...',
    'auth.noAccount': 'アカウントをお持ちでないですか？',
    'auth.hasAccount': 'すでにアカウントをお持ちですか？',
    'auth.email': 'メール',
    'auth.password': 'パスワード',
    'auth.displayName': '表示名（任意）',
    'auth.forgotPassword': 'パスワードをお忘れですか？',
    'auth.resetPassword': 'パスワードをリセット',
    'auth.resetPasswordDesc': 'メールアドレスを入力すると、リセットリンクを送信します。',
    'auth.sendResetEmail': 'リセットメールを送信',
    'auth.sendingResetEmail': '送信中...',
    'auth.resetEmailSent': 'メールを送信しました',
    'auth.resetEmailSentDesc': 'リセットリンクを確認するためにメールをご確認ください。',
    'auth.resetFailed': 'リセットに失敗しました',
    'auth.backToLogin': 'ログインに戻る',
    'auth.setNewPassword': '新しいパスワードを設定',
    'auth.setNewPasswordDesc': '以下に新しいパスワードを入力してください。',
    'auth.newPassword': '新しいパスワード',
    'auth.confirmPassword': 'パスワードを確認',
    'auth.passwordsDoNotMatch': 'パスワードが一致しません',
    'auth.updatePassword': 'パスワードを更新',
    'auth.updatingPassword': '更新中...',
    'auth.updatePasswordFailed': '更新に失敗しました',
    'auth.passwordUpdated': 'パスワードが更新されました',
    'auth.passwordUpdatedDesc': 'パスワードが正常に更新されました。',
    'auth.redirectingHome': 'ホームページにリダイレクトしています...',
    'auth.resetLinkExpired': 'リンクが期限切れです',
    'auth.resetLinkExpiredDesc': '新しいリンクをリクエストしてください。',
    'auth.verifyEmailReminder': 'すべての機能にアクセスするには、メールアドレスを確認してください。',
    'auth.resendVerification': '再送信',
    'auth.verificationSent': '確認メールを送信しました',
    'auth.verificationSentDesc': '受信トレイを確認してください。',
    'auth.resendFailed': '送信に失敗しました',
    'auth.sending': '送信中...',
    'auth.passwordStrength': 'パスワード強度',
    'auth.strengthWeak': '弱い',
    'auth.strengthMedium': '普通',
    'auth.strengthStrong': '強い',
    'auth.passwordRequirements': 'パスワードの要件:',
    'auth.req8chars': '8文字以上',
    'auth.reqUppercase': '大文字1つ',
    'auth.reqLowercase': '小文字1つ',
    'auth.reqNumber': '数字1つ',
    'auth.reqSpecial': '特殊文字1つ',
    'auth.emailValid': '有効なメール',
    'auth.emailInvalid': '無効なメール形式',
    'auth.passwordsMatch': 'パスワードが一致',
    'auth.passwordsNoMatch': 'パスワードが一致しません',

    // Common
    'common.loading': '読み込み中...',
    'common.error': 'エラー',
    'common.success': '成功',
    'common.save': '保存',
    'common.cancel': 'キャンセル',
    'common.delete': '削除',
    'common.loginToSave': 'ログインして保存',
    'common.dismiss': '閉じる',
    'checklist.copy': 'コピー',
    'checklist.print': '印刷',
    'checklist.addItem': '項目を追加',

    // Profile
    'profile.title': 'プロフィール設定',
    'profile.subtitle': 'アカウント情報を管理',
    'profile.picture': 'プロフィール写真',
    'profile.pictureDesc': '画像をアップロード（最大5MB）',
    'profile.uploadImage': '画像をアップロード',
    'profile.uploading': 'アップロード中...',
    'profile.accountInfo': 'アカウント情報',
    'profile.accountInfoDesc': '表示名を更新',
    'profile.displayName': '表示名',
    'profile.emailAddress': 'メールアドレス',
    'profile.emailNote': 'メールアドレスは変更できません',
    'profile.saveChanges': '変更を保存',
    'profile.saving': '保存中...',
    'profile.dangerZone': '危険ゾーン',
    'profile.dangerZoneDesc': 'アカウントに対する取り消し不可能な操作',
    'profile.deleteAccount': 'アカウントを削除',
    'profile.deleteAccountWarning': 'アカウントを削除すると、すべてのデータが完全に削除されます。この操作は取り消せません。',
    'profile.deletingAccount': '削除中...',
    'profile.deleteAccountConfirmTitle': '本当によろしいですか？',
    'profile.deleteAccountConfirmDesc': 'これにより、保存されたリスト、サブスクリプション、プロフィール情報を含むすべてのデータが完全に削除されます。',
    'profile.confirmDelete': 'はい、アカウントを削除します',
    'profile.accountDeleted': 'アカウントが削除されました',
    'profile.accountDeletedDesc': 'アカウントと関連するすべてのデータが完全に削除されました。',
    'profile.deleteAccountError': 'アカウントの削除に失敗しました。もう一度お試しください。',

    // Footer
    'footer.tagline': 'あらゆる状況に対応したAI生成チェックリストのリソース。',
    'footer.quickLinks': 'クイックリンク',
    'footer.allCategories': 'すべてのカテゴリー',
    'footer.generateChecklist': 'チェックリスト作成',
    'footer.popularCategories': '人気カテゴリー',
    'footer.copyright': '© {year} Nexihow. All rights reserved.',
    'footer.homeLife': '家庭 & 生活',
    'footer.travel': '旅行',
    'footer.workBusiness': '仕事 & ビジネス',

    // 404 Page
    'notFound.title': '404',
    'notFound.message': 'ページが見つかりません',
    'notFound.returnHome': 'ホームに戻る',

    // Search Page
    'search.title': '検索結果',
    'search.resultsFor': '"{query}"の検索結果: {count}件',
    'search.noResults': '結果が見つかりません',
    'search.noResultsDesc': '別の検索語を試すか、カテゴリーを参照してください。',
    'search.browseCategories': 'カテゴリーを見る',
    'search.generateCustom': 'カスタムリストを作成',

    // Category Page
    'category.notFound': 'カテゴリーが見つかりません',
    'category.notFoundDesc': 'お探しのカテゴリーは存在しません。',
    'category.browseAll': 'すべてのカテゴリーを見る',
    'category.checklistsAvailable': '{count}件のチェックリスト',
    'category.noChecklists': 'このカテゴリーにはまだリストがありません。',
    'category.generateOne': 'AIで作成する',

    // Checklist Page
    'checklistPage.notFound': 'チェックリストが見つかりません',
    'checklistPage.notFoundDesc': 'お探しのリストは存在しません。',
    'checklistPage.browseAll': 'すべてのリストを見る',
    'checklistPage.sound': 'サウンド',
    'checklistPage.soundOn': 'オン',
    'checklistPage.soundOff': 'オフ',
    'checklistPage.ofCompleted': '{total}中{checked}完了',
    'checklistPage.due': '期限: {date}',
    'checklistPage.congratulations': 'おめでとうございます！',
    'checklistPage.completedMessage': 'このリストを完了しました！',
    'checklistPage.saveCopy': 'コピーを保存',
    'checklistPage.shareAchievement': '達成を共有',
    'checklistPage.relatedChecklists': '関連リスト',

    // Saved Checklist View
    'savedView.notFound': 'リストが見つかりません',
    'savedView.backToSaved': '保存済みリストに戻る',
    'savedView.savedOn': '保存日: {date}',

    // Breadcrumbs
    'breadcrumb.home': 'ホーム',
    'breadcrumb.categories': 'カテゴリー',
    'breadcrumb.searchResults': '検索結果',
    'breadcrumb.myChecklists': 'マイリスト',

    // FAQ
    'faq.whatIs.question': 'Nexihowとは何ですか？',
    'faq.whatIs.answer': 'Nexihowは、引っ越しから旅行、仕事からウェルネスまで、あらゆる生活状況に対応したAI生成チェックリストを無料で提供するプラットフォームです。',
    'faq.howToCreate.question': 'カスタムリストはどうやって作成しますか？',
    'faq.howToCreate.answer': '作成ページに移動し、トピックを入力するだけで、AIが数秒であなたのニーズに合わせたカスタムリストを作成します。',
    'faq.isFree.question': 'チェックリストは無料ですか？',
    'faq.isFree.answer': 'はい！基本的なチェックリストへのアクセスと作成は完全に無料です。無制限保存やPDFエクスポートなどのプレミアム機能はサブスクリプションで利用できます。',
    'faq.canSave.question': 'リストを保存して共有できますか？',
    'faq.canSave.answer': 'はい、アカウントにリストを保存したり、コピー、印刷、またはソーシャルメディアや直接リンクで共有できます。',

    // Additional missing keys
    'saved.countSaved': '{count}件のリストを保存',
    'saved.moreItemsCount': '+{count}件のアイテム',
    'profile.memberSince': '登録日: {date}',
    'profile.characters': '{count}/{max}文字',
    'profile.fileTypeError': '無効なファイル形式',
    'profile.fileTypeErrorDesc': 'JPG、PNG、WebP、またはGIF画像をアップロードしてください。',
    'profile.fileSizeError': 'ファイルが大きすぎます',
    'profile.fileSizeErrorDesc': '5MB未満の画像をアップロードしてください。',
    'profile.fileFormatHint': 'JPG、PNG、WebP、またはGIF。最大5MB。',
    'generate.enterItem': 'アイテムを入力...',
    'dashboard.days': '日',
    'dashboard.overdueCount': '{count}件の期限切れリマインダー',
    'dashboard.moreRemindersCount': '+ {count}件のリマインダー',
    'locale.selectLanguage': '言語',
  },
};

export const getTranslation = (
  key: string,
  locale: string,
  params?: Record<string, string | number>
): string => {
  const localeTranslations = translations[locale] || translations.en;
  let text = localeTranslations[key] || translations.en[key] || key;

  // Replace parameters like {year}
  if (params) {
    Object.entries(params).forEach(([param, value]) => {
      text = text.replace(`{${param}}`, String(value));
    });
  }

  return text;
};
